import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isBefore,
  startOfMonth,
  startOfToday,
} from "date-fns";
import { api, formatApiError, getTracking, SERVICES, FABRICS, LOCATIONS, prettyTime } from "@/lib/api";

const STEPS = ["Commission", "Cloth", "Date", "Time", "Details", "Review"];
const EASE = [0.16, 1, 0.3, 1];

const StepShell = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.5, ease: EASE }}
  >
    {children}
  </motion.div>
);

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const initialService = searchParams.get("service") || "";

  const [step, setStep] = useState(initialService ? 1 : 0);
  const [service, setService] = useState(initialService);
  const [fabric, setFabric] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "Atelier Studio", notes: "" });
  const [month, setMonth] = useState(startOfToday());
  const [availability, setAvailability] = useState({});
  const [loadingCal, setLoadingCal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const fetchAvailability = async () => {
      setLoadingCal(true);
      try {
        const { data } = await api.get(`/availability?month=${format(month, "yyyy-MM")}`);
        setAvailability(data.days);
      } catch (e) {
        toast.error("Could not load availability. Please try again.");
      } finally {
        setLoadingCal(false);
      }
    };
    fetchAvailability();
  }, [month]);

  const today = startOfToday();
  const monthDays = useMemo(
    () => eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) }),
    [month]
  );
  const offset = (getDay(startOfMonth(month)) + 6) % 7;
  const slotsForDate = availability[date] || [];
  const selectedService = SERVICES.find((s) => s.id === service);

  const canContinue = () => {
    if (step === 0) return !!service;
    if (step === 1) return true;
    if (step === 2) return !!date;
    if (step === 3) return !!time;
    if (step === 4) return form.name.trim() && /.+@.+\..+/.test(form.email) && form.phone.trim();
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        service, fabric, date, time,
        name: form.name, email: form.email, phone: form.phone,
        location: form.location, notes: form.notes,
        tracking: getTracking(),
      });
      setResult(data);
      toast.success("Your fitting request has been received.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="flex min-h-screen flex-col bg-[#FFFFFF]" data-testid="booking-success-page">
        <BookingHeader />
        <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center px-6 py-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#2D5A3F]">
              <Check size={22} className="text-white" />
            </div>
            <h1 className="mt-8 font-serif text-4xl font-light tracking-tight text-[#1A1917] sm:text-5xl">
              Consider it measured.
            </h1>
            <p className="mt-4 text-base font-light leading-relaxed text-[#524F4A]">
              Thank you, {result.name.split(" ")[0]}. Your request has been received and a
              confirmation is on its way to your inbox. We will confirm your appointment shortly.
            </p>
            <div className="mt-10 border border-[#E5DEC9] bg-white p-8" data-testid="booking-success-summary">
              <Row label="Reference" value={result.booking_id} mono />
              <Row label="Commission" value={result.service_name} />
              <Row label="Date" value={format(new Date(result.date + "T12:00:00"), "EEEE, MMMM d, yyyy")} />
              <Row label="Time" value={prettyTime(result.time)} />
              <Row label="Location" value={result.location} />
            </div>
            <Link
              to="/"
              data-testid="booking-success-home-button"
              className="mt-10 inline-flex items-center gap-3 bg-[#1A1917] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#FFFFFF] transition-colors duration-200 hover:bg-[#8C6D38] active:scale-95"
            >
              Return to the Atelier
            </Link>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="booking-page">
      <BookingHeader />

      <main className="mx-auto max-w-4xl px-6 py-16 sm:px-12">
        {/* Progress */}
        <div className="mb-14" data-testid="booking-progress">
          <div className="flex items-center justify-between">
            {STEPS.map((label, i) => (
              <div key={label} className="flex flex-1 items-center last:flex-none">
                <div className="flex flex-col items-start">
                  <span
                    className={`font-mono text-[10px] tracking-[0.2em] ${i <= step ? "text-[#8C6D38]" : "text-[#B9B4AB]"}`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className={`mt-1 hidden font-mono text-[10px] uppercase tracking-[0.2em] sm:block ${i <= step ? "text-[#1A1917]" : "text-[#B9B4AB]"}`}
                  >
                    {label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`mx-3 h-px flex-1 ${i < step ? "bg-[#8C6D38]" : "bg-[#E5DEC9]"}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <StepShell key="s0">
              <StepTitle title="What shall we make for you?" sub="Choose your commission — each begins with a private consultation." />
              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                {SERVICES.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`booking-service-${s.id}`}
                    onClick={() => setService(s.id)}
                    className={`border p-6 text-left transition-colors duration-200 active:scale-95 ${
                      service === s.id
                        ? "border-[#1A1917] bg-[#1A1917] text-[#FFFFFF]"
                        : "border-[#E5DEC9] bg-white hover:border-[#1A1917]"
                    }`}
                  >
                    <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${service === s.id ? "text-[#C59B27]" : "text-[#8C6D38]"}`}>
                      {s.time}
                    </p>
                    <p className="mt-2 font-serif text-xl font-medium">{s.name}</p>
                  </button>
                ))}
              </div>
            </StepShell>
          )}

          {step === 1 && (
            <StepShell key="s1">
              <StepTitle title="A preference of cloth?" sub="Optional — Reza will guide you through the fabric books either way." />
              <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {FABRICS.map((f) => (
                  <button
                    key={f}
                    data-testid={`booking-fabric-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setFabric(f)}
                    className={`border p-5 text-left font-serif text-lg transition-colors duration-200 active:scale-95 ${
                      fabric === f
                        ? "border-[#1A1917] bg-[#1A1917] text-[#FFFFFF]"
                        : "border-[#E5DEC9] bg-white text-[#1A1917] hover:border-[#1A1917]"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </StepShell>
          )}

          {step === 2 && (
            <StepShell key="s2">
              <StepTitle title="Choose your day" sub="Available Monday through Saturday, by appointment only." />
              <div className="mt-10 border border-[#E5DEC9] bg-white p-6 sm:p-8" data-testid="booking-calendar">
                <div className="flex items-center justify-between">
                  <button
                    data-testid="calendar-prev-month"
                    onClick={() => setMonth(addMonths(month, -1))}
                    disabled={isBefore(endOfMonth(month), today)}
                    className="p-2 text-[#524F4A] transition-colors hover:text-[#1A1917] disabled:opacity-30"
                    aria-label="Previous month"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <p className="font-serif text-xl font-medium text-[#1A1917]">{format(month, "MMMM yyyy")}</p>
                  <button
                    data-testid="calendar-next-month"
                    onClick={() => setMonth(addMonths(month, 1))}
                    className="p-2 text-[#524F4A] transition-colors hover:text-[#1A1917]"
                    aria-label="Next month"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
                <div className="mt-6 grid grid-cols-7 gap-1 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-[#858078]">
                  {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
                    <div key={d} className="py-2">{d}</div>
                  ))}
                </div>
                <div className={`grid grid-cols-7 gap-1 ${loadingCal ? "opacity-40" : ""}`}>
                  {Array.from({ length: offset }).map((_, i) => (
                    <div key={`pad-${i}`} />
                  ))}
                  {monthDays.map((d) => {
                    const ds = format(d, "yyyy-MM-dd");
                    const slots = availability[ds] || [];
                    const disabled = isBefore(d, today) || slots.length === 0;
                    const selected = date === ds;
                    return (
                      <button
                        key={ds}
                        data-testid={`booking-calendar-day-${ds}`}
                        disabled={disabled}
                        onClick={() => {
                          setDate(ds);
                          setTime("");
                          setStep(3);
                        }}
                        className={`aspect-square border text-sm transition-colors duration-150 ${
                          selected
                            ? "border-[#1A1917] bg-[#1A1917] text-[#FFFFFF]"
                            : disabled
                              ? "border-transparent text-[#D6D0C4]"
                              : "border-transparent text-[#1A1917] hover:border-[#1A1917]"
                        }`}
                      >
                        {format(d, "d")}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#858078]">
                  {loadingCal ? "Checking the appointment book…" : "Greyed dates are fully booked or unavailable"}
                </p>
              </div>
            </StepShell>
          )}

          {step === 3 && (
            <StepShell key="s3">
              <StepTitle
                title="Choose your hour"
                sub={date ? format(new Date(date + "T12:00:00"), "EEEE, MMMM d, yyyy") : ""}
              />
              <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="booking-slots">
                {slotsForDate.length === 0 && (
                  <p className="col-span-full text-sm font-light text-[#524F4A]">
                    No appointments remain this day — please choose another date.
                  </p>
                )}
                {slotsForDate.map((t) => (
                  <button
                    key={t}
                    data-testid={`booking-time-slot-${t.replace(":", "")}`}
                    onClick={() => setTime(t)}
                    className={`border px-6 py-4 font-mono text-sm tracking-[0.1em] transition-colors duration-200 active:scale-95 ${
                      time === t
                        ? "border-[#1A1917] bg-[#1A1917] text-[#FFFFFF]"
                        : "border-[#E5DEC9] bg-white text-[#1A1917] hover:border-[#1A1917]"
                    }`}
                  >
                    {prettyTime(t)}
                  </button>
                ))}
              </div>
            </StepShell>
          )}

          {step === 4 && (
            <StepShell key="s4">
              <StepTitle title="A few details" sub="So we may confirm your appointment and prepare for your visit." />
              <div className="mt-10 grid gap-6 sm:grid-cols-2">
                <Field label="Full Name" testid="booking-input-name">
                  <input
                    data-testid="booking-input-name-field"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="James Sterling"
                    className="w-full border border-[#E5DEC9] bg-white px-4 py-3 text-sm text-[#1A1917] outline-none transition-colors focus:border-[#1A1917]"
                  />
                </Field>
                <Field label="Email">
                  <input
                    data-testid="booking-input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    className="w-full border border-[#E5DEC9] bg-white px-4 py-3 text-sm text-[#1A1917] outline-none transition-colors focus:border-[#1A1917]"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    data-testid="booking-input-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="(858) 555-0100"
                    className="w-full border border-[#E5DEC9] bg-white px-4 py-3 text-sm text-[#1A1917] outline-none transition-colors focus:border-[#1A1917]"
                  />
                </Field>
                <Field label="Fitting Location">
                  <select
                    data-testid="booking-input-location"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full border border-[#E5DEC9] bg-white px-4 py-3 text-sm text-[#1A1917] outline-none transition-colors focus:border-[#1A1917]"
                  >
                    {LOCATIONS.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Notes — occasion, timeline, anything we should know">
                    <textarea
                      data-testid="booking-input-notes"
                      rows={4}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="A wedding in October, a new boardroom wardrobe…"
                      className="w-full border border-[#E5DEC9] bg-white px-4 py-3 text-sm text-[#1A1917] outline-none transition-colors focus:border-[#1A1917]"
                    />
                  </Field>
                </div>
              </div>
            </StepShell>
          )}

          {step === 5 && (
            <StepShell key="s5">
              <StepTitle title="Review your fitting" sub="Confirm the details below — we will take it from here." />
              <div className="mt-10 border border-[#E5DEC9] bg-white p-8" data-testid="booking-review">
                <Row label="Commission" value={selectedService?.name} />
                <Row label="Cloth preference" value={fabric || "Open to guidance"} />
                <Row label="Date" value={format(new Date(date + "T12:00:00"), "EEEE, MMMM d, yyyy")} />
                <Row label="Time" value={prettyTime(time)} />
                <Row label="Client" value={form.name} />
                <Row label="Email" value={form.email} />
                <Row label="Phone" value={form.phone} />
                <Row label="Location" value={form.location} />
                {form.notes && <Row label="Notes" value={form.notes} />}
              </div>
            </StepShell>
          )}
        </AnimatePresence>

        {/* Nav */}
        <div className="mt-14 flex items-center justify-between">
          <span />
          {step < 5 ? (
            <button
              data-testid="booking-next-button"
              onClick={() => canContinue() && setStep(step + 1)}
              disabled={!canContinue()}
              className="inline-flex items-center gap-3 bg-[#1A1917] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#FFFFFF] transition-colors duration-200 hover:bg-[#8C6D38] active:scale-95 disabled:cursor-not-allowed disabled:opacity-30"
            >
              Continue <ArrowRight size={14} />
            </button>
          ) : (
            <button
              data-testid="booking-submit-button"
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-3 bg-[#8C6D38] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-200 hover:bg-[#1A1917] active:scale-95 disabled:opacity-50"
            >
              {submitting ? "Reserving…" : "Request Appointment"} <ArrowRight size={14} />
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

function BookingHeader() {
  return (
    <header className="border-b border-[#E5DEC9]/70 bg-[#FFFFFF]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-12 lg:px-20">
        <Link to="/" data-testid="booking-logo" className="flex items-center gap-3">
          <img src="/images/logo-mark.png" alt="Bespoke Maestro logo" className="h-10 w-auto" />
          <span className="flex flex-col">
            <span className="font-serif text-xl font-semibold tracking-wide text-[#1A1917]">Bespoke Maestro</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#858078]">Private Fitting</span>
          </span>
        </Link>
        <Link
          to="/"
          data-testid="booking-exit-link"
          className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#524F4A] transition-colors hover:text-[#1A1917]"
        >
          Exit
        </Link>
      </div>
    </header>
  );
}

function StepTitle({ title, sub }) {
  return (
    <div>
      <h1 className="font-serif text-3xl font-light tracking-tight text-[#1A1917] sm:text-4xl">{title}</h1>
      {sub && <p className="mt-3 text-base font-light text-[#524F4A]">{sub}</p>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-[#EDE8DB] py-3 last:border-0">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">{label}</span>
      <span className={`text-right text-sm text-[#1A1917] ${mono ? "font-mono tracking-[0.15em] text-[#8C6D38]" : ""}`}>
        {value}
      </span>
    </div>
  );
}
